import { z } from 'zod';

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from './store';

export const ImageSchema = z.object({
  projectId: z.string(),
  imageId: z.number(),
  name: z.string(),
  blobUrl: z.string().url("Invalid URL"),
  size: z.number(),
  type: z.string(),
  lastModified: z.number().default(Date.now()),
  progress: z.number().default(0),
  timeUpload: z.number().optional(),
  errorMsg: z.string().array().default([]),
  isTma: z.boolean().optional(),
  estCoreDiameterInMm: z.number().optional(),
  // server-side image id, known once the upload-url call returns; the image list
  // polls until this id shows up, so it knows when the row has landed
  serverId: z.string().optional(),
});

export type Image = z.infer<typeof ImageSchema>;
export type ImageOut = Omit<Image, "lastModified" | "progress" | "timeUpload" | "errorMsg">;
export type ImageArg = { id?: number; status: ImageStatus };
export type ImageStatus = "queue" | "inprog" | "done";

interface ImageQueue {
  queue: Image[];
  inprog: Image[];
  done: Image[];
}

const initialState: ImageQueue = { queue: [], inprog: [], done: [] };

const imageQueueSlice = createSlice({
  name: "imageQueue",
  initialState: initialState,
  reducers: {
    addImage: (state, action: PayloadAction<ImageOut>) => {
      let img = action.payload;
      const result = ImageSchema.safeParse({
        ...img,
        lastModified: Date.now(),
        progress: 0,
        errorMsg: []
      });
      if (result.success) state.queue.push(result.data);
      else
        state.done.push({
          ...img,
          lastModified: Date.now(),
          progress: 100,
          errorMsg: result.error.issues.map((s) => s.message),
        });
    },
    queueImage: (state, action: PayloadAction<ImageOut>) => {
      let img = action.payload;
      const updateImage = state.queue.find((_img) => _img.imageId === img.imageId);
      if (updateImage) {
        console.log("Starting upload w/ " + img.imageId);
        state.queue = state.queue.filter((_img) => _img.imageId !== img.imageId);
        state.inprog.push({
          ...img,
          lastModified: Date.now(),
          progress: 0,
          errorMsg: []
        });
      }
    },
    updateImageProg: (state, action: PayloadAction<ImageOut & { progress: number }>) => {
      let img = action.payload;
      const updateImage = state.inprog.find((_img) => _img.imageId === img.imageId);

      if (updateImage)
        if (img.progress < 100) {
          updateImage.progress = img.progress;
        } else {
          console.log("Image " + img.imageId + " is completed.");
          state.inprog = state.inprog.filter((_img) => _img.imageId !== img.imageId);
          state.done.push({
            ...img,
            lastModified: Date.now(),
            progress: 100,
            timeUpload: Date.now(),
            errorMsg: []
          });
        }
    },
    // Moves an image out of "queue"/"inprog" into "done" with an errorMsg,
    // for uploads that fail before ever reaching 100% (e.g. quota exceeded).
    // Without this the item just sits in "inprog" at whatever progress it had.
    failImage: (state, action: PayloadAction<{ imageId: number; errorMsg: string }>) => {
      const { imageId, errorMsg } = action.payload;
      const img = state.inprog.find((_img) => _img.imageId === imageId)
        ?? state.queue.find((_img) => _img.imageId === imageId);
      if (!img) return;
      state.inprog = state.inprog.filter((_img) => _img.imageId !== imageId);
      state.queue = state.queue.filter((_img) => _img.imageId !== imageId);
      state.done.push({
        ...img,
        lastModified: Date.now(),
        progress: 100,
        errorMsg: [errorMsg],
      });
    },
    removeImage: (state, action: PayloadAction<ImageArg>) => {
      let { id, status } = action.payload;
      if (id) state[status] = state[status].filter((_img) => _img.imageId !== id);
      else state[status] = [];
    },
    // Retire finished uploads by their server id, once the image list has caught
    // up with them or the image was deleted. Without this their stand-in rows
    // outlive whatever they were standing in for.
    clearDoneByServerIds: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload);
      state.done = state.done.filter((_img) => !_img.serverId || !ids.has(_img.serverId));
    },
  },
});

export const selectAllImageQueue = (state: RootState) => state.imageQueue;
export const getImageByStatus = (state: RootState, status: ImageStatus) => state.imageQueue[status];
export const getImageByProject = (state: RootState, projectId: string) => {
  const queue = state.imageQueue.queue.filter((img) => img.projectId === projectId);
  const inprog = state.imageQueue.inprog.filter((img) => img.projectId === projectId);
  const done = state.imageQueue.done.filter((img) => img.projectId === projectId);
  return { queue, inprog, done };
};

export const {
  addImage, queueImage, updateImageProg, failImage, removeImage, clearDoneByServerIds
} = imageQueueSlice.actions;
export default imageQueueSlice.reducer;
